import { useState, useCallback, useRef, useEffect } from 'react';
import { Drawer, Form, Select, Button, Row, Col, message, Modal, Tooltip, Upload, Image, Input, Pagination } from 'antd';
// import { UploadOutlined, CloseCircleOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';
import './index.less';
import { fileApi } from '@services/Conference';
import { getCaseScoreList, getPCRankingSetting, getScoreStageByIdApi, getScoreTopicsListApi } from '@services/Selection';
import { prepareBCOSSUrl } from '@utils/utils';

/**
 * 线下 LED 屏投屏用素材生成
 * 1. 图片尺寸不一，现定一个确定量，即图片高定为 1000px，图片垫在内容下方，高 1000px，宽 auto，
 *    再动态调整图片容器和内容容器的宽高和图片相同；
 * 2. 内容区榜单表格通过数据量的判断，加3个档位的 padding 和 fontSize 的适配。
 */

const DEFAULT_PREVIEW_SIZE = {
    width: 1800,
    height: 1000,
};

const colorList = ['#f5222d', '#fa541c', '#fa8c16', '#52c41a', '#1677ff', '#0327ff', '#722ed1', '#eb2f96', '#333333', '#888888', '#ffffff'].map(c => c.toUpperCase());

const ExportPCRankingDrawer = ({ open, onClose, title = '导出PC榜单', projectInfo = {} }) => {
    const [form] = Form.useForm();
    const [styleForm] = Form.useForm();
    const [styleModalVisible, setStyleModalVisible] = useState(false);
    const [topicList, setTopicList] = useState([]);
    const [topicLoading, setTopicLoading] = useState(false);
    const [styleSettings, setStyleSettings] = useState({
        bg_img_url: '',
        topic_text_color: '',
        awards_text_color: '',
        th_bg_color: '',
    });
    const [hasStyleSettings, setHasStyleSettings] = useState(true);
    const [pcBackgroundUrlList, setPcBackgroundUrlList] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [rankingData, setRankingData] = useState([]);
    const [allRankingData, setAllRankingData] = useState([]);
    const [awardOptions, setAwardOptions] = useState([]);
    const [stageName, setStageName] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(15);
    const isAllowCheck = useRef(null);
    const optionHeaders = { headers: { 'Content-Type': 'multipart/form-data' } };
    const [previewSize, setPreviewSize] = useState(DEFAULT_PREVIEW_SIZE);

    const topicId = Form.useWatch('topicId', form);
    const selectedAward = Form.useWatch('award', form);

    // 当Drawer关闭时清空数据
    useEffect(() => {
        if (!open) {
            form.resetFields();
            setRankingData([]);
            setCurrentPage(1);
        }
    }, [open, form]);

    // 从接口加载主题列表
    useEffect(() => {
        if (open && projectInfo?.projectId) {
            setTopicLoading(true);
            getScoreTopicsListApi({ projectId: projectInfo.projectId, stageId: projectInfo.stageId })
                .then(res => {
                    if (res?.status_code === 200 && res.data) {
                        const topics = res.data.map(item => ({
                            label: item.topicName,
                            value: item.topicId,
                        }));
                        setTopicList(topics);
                    }
                })
                .catch(error => {
                    console.error('Failed to load topics:', error);
                    message.error('加载主题列表失败');
                })
                .finally(() => {
                    setTopicLoading(false);
                });
        }
    }, [open, projectInfo?.projectId, projectInfo?.stageId]);

    // 加载榜单数据
    useEffect(() => {
        if (topicId && projectInfo?.projectId && projectInfo?.stageId) {
            setDataLoading(true);
            form.setFieldValue('award', undefined);
            getCaseScoreList({
                projectId: projectInfo.projectId,
                stageId: projectInfo.stageId,
                topicId: topicId,
                platformType: 'PC',
            })
                .then(res => {
                    if (res?.status_code === 200 && res.data?.list) {
                        const formattedData = res.data.list.map(item => ({
                            key: item.caseId,
                            caseNo: item.caseId,
                            caseName: item.caseName,
                            submissionUnit: item.hosName || '提报单位',
                            rewardName: item.rewardName || '',
                        }));
                        setAllRankingData(formattedData);

                        // 提取并去重奖项名称
                        const uniqueAwards = Array.from(new Map(formattedData.filter(item => item.rewardName).map(item => [item.rewardName, item.rewardName])).values());
                        const awardOptionsList = uniqueAwards.map(award => ({
                            label: award,
                            value: award,
                        }));
                        setAwardOptions(awardOptionsList);
                    } else {
                        setAllRankingData([]);
                        setAwardOptions([]);
                    }
                })
                .catch(error => {
                    setAllRankingData([]);
                    setAwardOptions([]);
                })
                .finally(() => {
                    setDataLoading(false);
                });
        } else {
            setAllRankingData([]);
            setAwardOptions([]);
        }
    }, [form, projectInfo?.projectId, projectInfo?.stageId, topicId]);

    // 当奖项选择变化时，筛选列表数据
    useEffect(() => {
        if (selectedAward) {
            const filteredData = allRankingData.filter(item => item.rewardName === selectedAward);
            setRankingData(filteredData);
        } else {
            setRankingData(allRankingData);
        }
        setCurrentPage(1);
    }, [selectedAward, allRankingData]);

    // 获取当前页显示的数据
    const getPaginatedData = () => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return rankingData.slice(startIndex, endIndex);
    };

    const handleDownloadImage = useCallback(async () => {
        const previewElement = document.querySelector('.pc-preview-section');
        if (!previewElement) {
            message.error('预览区域未找到');
            return;
        }

        try {
            message.loading({ content: '正在生成图片...', key: 'downloadImage' });

            // 等待背景图加载完成
            await new Promise(resolve => setTimeout(resolve, 500));
            const { width: previewWidth, height: previewHeight } = previewElement.getBoundingClientRect();

            const canvas = await html2canvas(previewElement, {
                useCORS: true,
                allowTaint: false,
                backgroundColor: null,
                scale: 3,
                width: previewWidth,
                height: previewHeight,
                logging: false,
                imageTimeout: 0,
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            // 生成文件名: 赛段_主题名称_奖项名称.png
            const topicName = topicList.find(t => t.value === topicId)?.label || '';
            const fileName = `${stageName}_${topicName}${selectedAward ? '_' + selectedAward : ''}.png`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success({ content: '图片下载成功', key: 'downloadImage' });
        } catch (error) {
            console.error('Download error:', error);
        }
    }, [projectInfo, styleSettings.bg_img_url, topicId, topicList, selectedAward, stageName]);

    useEffect(() => {
        if (!styleSettings.bg_img_url) {
            setPreviewSize(DEFAULT_PREVIEW_SIZE);
        }
    }, [styleSettings.bg_img_url]);

    const handleBgImageLoad = useCallback(event => {
        const { naturalWidth, naturalHeight } = event.target;
        if (!naturalWidth || !naturalHeight) {
            return;
        }
        const targetHeight = DEFAULT_PREVIEW_SIZE.height;
        const nextHeight = targetHeight;
        const scale = targetHeight / naturalHeight;
        const nextWidth = Math.round(naturalWidth * scale);
        setPreviewSize({
            width: nextWidth,
            height: nextHeight,
        });
    }, []);

    // 加载样式设置
    useEffect(() => {
        if (open && projectInfo?.stageId) {
            getScoreStageByIdApi({ id: projectInfo.stageId })
                .then(res => {
                    if (res?.status_code === 200 && res.data) {
                        const stageData = res.data;
                        // 获取赛段名称
                        setStageName(stageData.name || '');
                        let settings = {
                            bg_img_url: '',
                            topic_text_color: '',
                            awards_text_color: '',
                            th_bg_color: '',
                        };

                        // 解析rankStyleSetting中的PC样式设置
                        if (stageData.rankStyleSetting) {
                            try {
                                const rankSettings = JSON.parse(stageData.rankStyleSetting);
                                if (rankSettings.pc) {
                                    settings = {
                                        ...settings,
                                        ...rankSettings.pc,
                                    };
                                    setHasStyleSettings(true);
                                } else {
                                    setHasStyleSettings(false);
                                }
                            } catch (error) {
                                console.error('Failed to parse rank style settings:', error);
                                setHasStyleSettings(false);
                            }
                        } else {
                            setHasStyleSettings(false);
                        }

                        setStyleSettings(settings);
                    }
                })
                .catch(error => {
                    console.error('Failed to load stage info:', error);
                    setHasStyleSettings(false);
                });
        }
    }, [open, projectInfo?.stageId]);

    const openStyleModal = () => {
        styleForm.setFieldsValue(styleSettings);
        if (styleSettings.bg_img_url) {
            setPcBackgroundUrlList([prepareBCOSSUrl(styleSettings.bg_img_url)]);
        }
        setStyleModalVisible(true);
    };

    const handleStyleModalOk = async () => {
        try {
            // 先验证表单
            await styleForm.validateFields();
            const values = styleForm.getFieldsValue();
            const settingJson = JSON.stringify({
                bg_img_url: values.bg_img_url || '',
                topic_text_color: values.topic_text_color || '',
                awards_text_color: values.awards_text_color || '',
                th_bg_color: values.th_bg_color || '',
            });

            const apiParams = {
                settingJson,
                stageId: projectInfo.stageId,
            };

            message.loading({ content: '样式设置保存中...', key: 'styleModalSave' });
            const res = await getPCRankingSetting(apiParams);

            if (res?.status_code === 200) {
                const newSettings = {
                    ...styleSettings,
                    ...values,
                };
                setStyleSettings(newSettings);
                setStyleModalVisible(false);
                message.success({ content: '样式设置保存成功', key: 'styleModalSave' });
                setHasStyleSettings(true);
            }
        } catch (error) {
            console.error('Save style settings error:', error);
        }
    };

    const changeBgColor = (color, fieldName) => {
        styleForm.setFieldValue(fieldName, color);
    };

    // 文件校验
    const checkout = (file, size) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
        if (!isJpgOrPng) {
            message.error('仅支持上传JPG/PNG格式的图片');
            isAllowCheck.current = true;
            return false;
        } else {
            isAllowCheck.current = false;
        }

        const isLtSize = file.size / 1024 / 1024 < size;
        if (!isLtSize) {
            message.error(`图片不能大于${size}MB`);
            isAllowCheck.current = true;
            return false;
        } else {
            isAllowCheck.current = false;
        }

        return isJpgOrPng && isLtSize;
    };

    // 通用背景图上传函数
    const uploadBackground = (info, setUrlList, fieldName) => {
        const formData = new FormData();
        formData.append('file', info.file);
        formData.append('type', 14);
        if (!isAllowCheck.current) {
            fileApi(formData, optionHeaders).then(res => {
                if (!res) {
                    message.error('文件上传失败');
                    return false;
                }
                if (res.status_code === 200) {
                    setUrlList([prepareBCOSSUrl(res.data)]);
                    styleForm.setFieldValue(fieldName, res.data);
                }
            });
        } else {
            styleForm.setFieldValue(fieldName, undefined);
        }
    };

    // 通用删除背景图函数
    const removeBackground = (setUrlList, fieldName) => {
        setUrlList([]);
        styleForm.setFieldValue(fieldName, '');
    };

    const uploadPcBackground = info => uploadBackground(info, setPcBackgroundUrlList, 'bg_img_url');
    const removePcBackground = () => removeBackground(setPcBackgroundUrlList, 'bg_img_url');

    const renderColorField = (fieldName, label) => {
        return (
            <Form.Item className="required" label={label} rules={[{ required: true, message: `${label}必填` }]}>
                <div className="color-field-wrap">
                    <div className="color-field">
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const color = getFieldValue(fieldName);
                                return !!color && <div className="color-previewer" style={{ backgroundColor: `${color}`, borderColor: '#CCCCCC' }} />;
                            }}
                        </Form.Item>

                        <Form.Item
                            noStyle
                            name={fieldName}
                            rules={[
                                { required: true, message: `${label}必填` },
                                { pattern: /^#[0-9a-f]{6}$/i, message: '色值不符合格式要求' },
                            ]}
                        >
                            <Input style={{ width: 120, border: 'none', boxShadow: 'none' }} maxLength={7} placeholder="#FFFFFF" />
                        </Form.Item>
                    </div>
                    <div className="color-list">
                        {colorList.map((item, index) => (
                            <Tooltip key={index} placement="top" title="点击使用该颜色">
                                <div onClick={() => styleForm.setFieldValue(fieldName, item)} className="color-previewer" style={{ backgroundColor: `${item}`, borderColor: '#CCCCCC' }} />
                            </Tooltip>
                        ))}
                    </div>
                </div>
            </Form.Item>
        );
    };

    const renderStyleForm = () => {
        return (
            <>
                <Form.Item name="bg_img_url" label="PC背景图" extra="图片高不小于2000px，支持JPG/PNG格式，大小不超过5MB">
                    {!!pcBackgroundUrlList.length && (
                        <div className="background-image-wrap">
                            <Image width={200} src={pcBackgroundUrlList[0]} style={{ marginBottom: 8 }} alt="PC背景图预览" />
                            <i className="bi bi-x-circle image-close-icon" onClick={removePcBackground} />
                        </div>
                    )}
                    <div>
                        <Upload beforeUpload={file => checkout(file, 5)} fileList={pcBackgroundUrlList} customRequest={uploadPcBackground} maxCount={1} showUploadList={false}>
                            <Button icon={<i className="bi bi-upload" />}>{!pcBackgroundUrlList.length ? '上传图片' : '更改图片'}</Button>
                        </Upload>
                    </div>
                </Form.Item>
                {renderColorField('topic_text_color', '主题文字颜色')}
                {renderColorField('awards_text_color', '荣誉名称文字颜色')}
                {renderColorField('th_bg_color', '表头背景色')}
            </>
        );
    };

    // 根据数据量调整表格单元格样式
    const AdjustTableCellStyle = {
        // 数据量小
        large: { padding: '16px 4px', fontSize: 20 },
        // 数据量中
        medium: { padding: '10px 4px', fontSize: 16 },
        // 数据量大
        small: { padding: '9px 4px', fontSize: 14 },
    };

    const getCellStyle = () => {
        return {
            color: styleSettings.topic_text_color || '',
            padding: rankingData?.length >= 15 ? AdjustTableCellStyle.small.padding : rankingData?.length >= 10 ? AdjustTableCellStyle.medium.padding : AdjustTableCellStyle.large.padding,
            fontSize: rankingData.length > 15 ? AdjustTableCellStyle.small.fontSize : rankingData?.length >= 10 ? AdjustTableCellStyle.medium.fontSize : AdjustTableCellStyle.large.fontSize,
        };
    };

    return (
        <>
            <Drawer
                closable={{ placement: 'end' }}
                title={title}
                placement="bottom"
                onClose={onClose}
                open={open}
                size="100vh"
                destroyOnHidden
                styles={{
                    body: {
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    },
                }}
                footer={null}
            >
                <div className="export-ranking-content">
                    <Form form={form}>
                        <Row gutter={16} align="middle" className="form-control-row">
                            <Col span={6}>
                                <Form.Item name="topicId" label="主题：" rules={[{ required: true, message: '请选择主题' }]} className="form-item-no-margin">
                                    <Select placeholder="请选择主题" options={topicList} loading={topicLoading} />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="award" label="荣誉名称" className="form-item-no-margin">
                                    <Select placeholder="请选择荣誉名称" options={awardOptions} allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item noStyle>{!!selectedAward && rankingData.length > pageSize && <Pagination showTotal={total => `共${total}条`} current={currentPage} pageSize={pageSize} total={rankingData.length} onChange={setCurrentPage} />}</Form.Item>
                            </Col>
                            <Col span={6} className="button-group">
                                <Button type="primary" onClick={openStyleModal}>
                                    样式设置
                                </Button>
                                <Button type="primary" onClick={handleDownloadImage}>
                                    下载PC榜单图
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    <div className="content-divider" />

                    <div className="pc-preview-section-box">
                        <div className="pc-preview-section" style={{ width: previewSize.width, height: previewSize.height }}>
                            {styleSettings.bg_img_url && <img className="pc-preview-bg" crossOrigin="anonymous" onLoad={handleBgImageLoad} src={prepareBCOSSUrl(styleSettings.bg_img_url)} alt="PC榜单背景图" />}
                            <div className="pc-preview-inner">
                                {!hasStyleSettings ? (
                                    <div className="pc-preview-empty">
                                        <div>
                                            <div>请先完成样式设置</div>
                                            <div className="pc-preview-empty-tip">点击上方"样式设置"按钮进行配置</div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* 预览标题区域 */}
                                        <div className="preview-header">
                                            {topicId && (
                                                <div className="topic-name" style={{ color: styleSettings.topic_text_color }}>
                                                    {topicList.find(t => t.value === topicId)?.label}
                                                </div>
                                            )}
                                            {selectedAward && (
                                                <div className="header-subtitle" style={{ color: styleSettings.awards_text_color }}>
                                                    {selectedAward}
                                                </div>
                                            )}
                                        </div>

                                        {selectedAward && (
                                            <div className="ranking-table">
                                                <div className="table-header" style={{ backgroundColor: styleSettings.th_bg_color }}>
                                                    <div className="table-cell header-cell col-case-no">实践编号</div>
                                                    <div className="table-cell header-cell col-case-name">实践名称</div>
                                                    <div className="table-cell header-cell col-unit">荣誉单位</div>
                                                </div>
                                                <div className="table-body">
                                                    {(dataLoading ? [] : getPaginatedData()).map(row => (
                                                        <div key={row.key} className="table-row" style={{ borderBottomColor: styleSettings.topic_text_color || '' }}>
                                                            <div className="table-cell col-case-no" style={{ ...getCellStyle() }}>
                                                                {row.caseNo}
                                                            </div>
                                                            <div
                                                                className="table-cell col-case-name"
                                                                style={{
                                                                    borderLeftColor: styleSettings.topic_text_color || '',
                                                                    borderRightColor: styleSettings.topic_text_color || '',
                                                                    ...getCellStyle(),
                                                                }}
                                                            >
                                                                {row.caseName}
                                                            </div>
                                                            <div className="table-cell col-unit" style={{ ...getCellStyle() }}>
                                                                {row.submissionUnit}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Drawer>

            <Modal
                title="PC榜单样式设置"
                open={styleModalVisible}
                onOk={handleStyleModalOk}
                onCancel={() => {
                    setStyleModalVisible(false);
                    setPcBackgroundUrlList([]);
                    styleForm.resetFields();
                }}
                width={800}
                zIndex={1001}
                className="export-ranking-style-modal"
            >
                <Form form={styleForm} labelCol={{ span: 5 }} wrapperCol={{ span: 19 }}>
                    {renderStyleForm()}
                </Form>
            </Modal>
        </>
    );
};

export default ExportPCRankingDrawer;
